import { Box, Skeleton, Stack } from '@mui/material';
import { Component } from 'react';
import { EwayField } from '../../components/billing/EwayField';
import { withLocale, WithLocaleProps } from '../../providers/LocaleProvider';

type EwayFieldType = 'name' | 'card' | 'expiry' | 'cvn';

const FIELD_TYPES: EwayFieldType[] = ['name', 'card', 'expiry', 'cvn'];

// Safety net: each EwayField reports readiness via onReady (the iframe 'load' event). If a load
// fires before its listener attaches the event could be missed, which would leave the skeleton up
// forever — reveal the fields anyway after this long.
const REVEAL_FALLBACK_MS = 5000;

class CreditCardPaymentBase extends Component<any & WithLocaleProps, any> {
  static ewayInjected = false;

  static defaultProps = {
    disabled: false,
    visible: false,
  };

  private revealTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: any) {
    super(props);

    this.state = {
      nameValid: false,
      cardValid: false,
      expiryValid: false,
      cvcValid: false,
      ewayInjcted: false,
      attemptId: props.attemptId,
      // Per-field iframe-load tracking so we can reveal all fields at once instead of one-by-one.
      fieldsLoaded: { name: false, card: false, expiry: false, cvn: false },
      forceReveal: false,
    };
  }

  componentDidMount() {
    EwayField.injectEwayScript(() => {
      this.setState({ ewayInjcted: true });
      this.startRevealTimer();
    });
  }

  componentWillUnmount() {
    if (this.revealTimer) clearTimeout(this.revealTimer);
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    if (prevProps.attemptId !== this.props.attemptId) {
      EwayField.resetEwayFields();

      this.setState({
        attemptId: this.props.attemptId,
        nameValid: false,
        cardValid: false,
        expiryValid: false,
        cvcValid: false,
        // Fields re-initialise on a new attempt — show the skeleton again until they reload.
        fieldsLoaded: { name: false, card: false, expiry: false, cvn: false },
        forceReveal: false,
      });
      this.startRevealTimer();
    }
  }

  startRevealTimer() {
    if (this.revealTimer) clearTimeout(this.revealTimer);
    this.revealTimer = setTimeout(() => this.setState({ forceReveal: true }), REVEAL_FALLBACK_MS);
  }

  onFieldReady(type: EwayFieldType) {
    this.setState((prev: any) => ({
      fieldsLoaded: { ...prev.fieldsLoaded, [type]: true },
    }));
  }

  isAllReady() {
    const { fieldsLoaded, forceReveal } = this.state;
    return forceReveal || FIELD_TYPES.every((t) => fieldsLoaded[t]);
  }

  // Propagate validity from the setState callback, not synchronously after setState. setState is
  // async, so reading this.state on the next line yields the PREVIOUS validity — the parent ends
  // up one event behind. Manual typing fires enough events that a later one carries the correct
  // value, but a single browser-autofill event would otherwise never propagate (leaving Pay Now
  // disabled until the user edits the field). The callback guarantees this.state is current.
  onNameChange(fieldValid: any, secureFieldCode: any) {
    this.setState({ nameValid: fieldValid }, () => {
      if (this.props.onChange) this.props.onChange(this.state, secureFieldCode);
    });
  }

  onCardChange(fieldValid: any, secureFieldCode: any) {
    this.setState({ cardValid: fieldValid }, () => {
      if (this.props.onChange) this.props.onChange(this.state, secureFieldCode);
    });
  }

  onExpiryChange(fieldValid: any, secureFieldCode: any) {
    this.setState({ expiryValid: fieldValid }, () => {
      if (this.props.onChange) this.props.onChange(this.state, secureFieldCode);
    });
  }

  onCVCChange(fieldValid: any, secureFieldCode: any) {
    this.setState({ cvcValid: fieldValid }, () => {
      if (this.props.onChange) this.props.onChange(this.state, secureFieldCode);
    });
  }

  renderFieldSkeleton() {
    return (
      <Stack gap='6px' sx={{ width: '100%' }}>
        <Skeleton variant='text' width={72} height={20} />
        <Skeleton variant='rounded' height={42} sx={{ borderRadius: '28px' }} />
      </Stack>
    );
  }

  render() {
    const { visible, translate } = this.props;
    const { ewayInjcted, attemptId } = this.state;
    const allReady = this.isAllReady();

    return (
      <div style={{ display: visible === true ? 'block' : 'none' }}>
        {ewayInjcted && (
          // Keep the eWAY field iframes rendered (they must be visible to load + fire onReady) and
          // cover them with an opaque skeleton overlay until every field has loaded. minHeight
          // stops the box collapsing before the iframes report a size.
          <Box sx={{ position: 'relative', minHeight: allReady ? undefined : 168 }}>
            {!allReady && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  bgcolor: '#fff',
                }}
              >
                <Stack gap={3}>
                  <Stack direction='row' gap={3} justifyContent='space-between'>
                    {this.renderFieldSkeleton()}
                    {this.renderFieldSkeleton()}
                  </Stack>
                  <Stack direction='row' gap={3} justifyContent='space-between'>
                    {this.renderFieldSkeleton()}
                    {this.renderFieldSkeleton()}
                  </Stack>
                </Stack>
              </Box>
            )}
            <Stack gap={3}>
              <Stack direction='row' gap={3} justifyContent='space-between'>
                <EwayField
                  label={translate('billing.nameOnCard')}
                  type='name'
                  attemptId={attemptId}
                  onChange={this.onNameChange.bind(this)}
                  onReady={() => this.onFieldReady('name')}
                  sx={{ width: '100%' }}
                />
                <EwayField
                  label={translate('billing.cardNumber')}
                  type='card'
                  attemptId={attemptId}
                  onChange={this.onCardChange.bind(this)}
                  onReady={() => this.onFieldReady('card')}
                  sx={{ width: '100%' }}
                />
              </Stack>
              <Stack direction='row' gap={3} justifyContent='space-between'>
                <EwayField
                  label={translate('billing.expiry')}
                  type='expiry'
                  attemptId={attemptId}
                  onChange={this.onExpiryChange.bind(this)}
                  onReady={() => this.onFieldReady('expiry')}
                  sx={{ width: '50%' }}
                />

                <EwayField
                  label={translate('billing.cvcCvv')}
                  type='cvn'
                  attemptId={attemptId}
                  onChange={this.onCVCChange.bind(this)}
                  onReady={() => this.onFieldReady('cvn')}
                  sx={{ width: '50%' }}
                />
              </Stack>
            </Stack>
          </Box>
        )}
      </div>
    );
  }
}

export const CreditCardPayment = withLocale(CreditCardPaymentBase);
